import React, { useEffect, useState, useRef } from 'react';
import { Calendar, ChevronDown, LayoutGrid, List, PieChart, X, RefreshCw, Layers } from 'lucide-react';
import { DateRangeKey, dateRangeOptions, loanStageOptions } from '../mockData';
const scopeOptions = ['All Loan Officers', 'By SVP', 'By Team Lead', 'Single Officer'];
const leadSourceOptions = ['Zillow', 'LendingTree', 'Realtor Partner', 'Past Client', 'Website', 'Social Media', 'Inbound Call', 'Other'];
interface FilterBarProps {
  activeView: 'overall' | 'bySource' | 'mix';
  onViewChange: (view: 'overall' | 'bySource' | 'mix') => void;
  dateRangeKey: DateRangeKey;
  onDateRangeChange: (key: DateRangeKey) => void;
  selectedScope: string;
  onScopeChange: (scope: string) => void;
  selectedSources: string[];
  onSourcesChange: (sources: string[]) => void;
  selectedStages: string[];
  onStagesChange: (stages: string[]) => void;
  includeInactive: boolean;
  onIncludeInactiveChange: (v: boolean) => void;
  onClear: () => void;
}
export function FilterBar({
  activeView,
  onViewChange,
  dateRangeKey,
  onDateRangeChange,
  selectedScope,
  onScopeChange,
  selectedSources,
  onSourcesChange,
  selectedStages,
  onStagesChange,
  includeInactive,
  onIncludeInactiveChange,
  onClear
}: FilterBarProps) {
  const [dateOpen, setDateOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [pendingSources, setPendingSources] = useState<string[]>(selectedSources);
  const [pendingStages, setPendingStages] = useState<string[]>(selectedStages);
  const dateRef = useRef<HTMLDivElement>(null);
  const scopeRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) {
        setDateOpen(false);
      }
      if (scopeRef.current && !scopeRef.current.contains(e.target as Node)) {
        setScopeOpen(false);
      }
      if (sourceRef.current && !sourceRef.current.contains(e.target as Node)) {
        setSourceOpen(false);
        setPendingSources(selectedSources);
      }
      if (stageRef.current && !stageRef.current.contains(e.target as Node)) {
        setStageOpen(false);
        setPendingStages(selectedStages);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [selectedSources, selectedStages]);
  const togglePendingSource = (source: string) => {
    setPendingSources(prev => prev.includes(source) ? prev.filter(s => s !== source) : [...prev, source]);
  };
  const applySourceFilter = () => {
    onSourcesChange(pendingSources);
    setSourceOpen(false);
  };
  const cancelSourceFilter = () => {
    setPendingSources(selectedSources);
    setSourceOpen(false);
  };
  const togglePendingStage = (id: string) => {
    setPendingStages(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const applyStageFilter = () => {
    onStagesChange(pendingStages);
    setStageOpen(false);
  };
  const cancelStageFilter = () => {
    setPendingStages(selectedStages);
    setStageOpen(false);
  };
  const sourceLabel = selectedSources.length === 0 ? 'All Sources' : selectedSources.length === 1 ? selectedSources[0] : `${selectedSources.length} Sources`;
  const stageLabel = selectedStages.length === 0 ? 'All Stages' : selectedStages.length === 1 ? loanStageOptions.find(o => o.id === selectedStages[0])?.label ?? '1 Stage' : `${selectedStages.length} Stages`;
  const dateLabel = dateRangeOptions.find(o => o.key === dateRangeKey)?.label ?? 'Last 30 days';
  return <div className="flex flex-wrap items-center justify-between gap-4 mb-8 text-sm" data-id="element-101">
      <div className="flex flex-wrap items-end gap-4" data-id="element-102">
        {/* Date Range */}
        <div className="flex flex-col relative" ref={dateRef} data-id="element-103">
          <span className="text-xs text-gray-500 mb-1 flex items-center gap-1 font-medium" data-id="element-104">
            <Calendar size={12} data-id="element-105" /> Date Range
          </span>
          <button onClick={() => {
          setDateOpen(!dateOpen);
          setScopeOpen(false);
          setSourceOpen(false);
          setStageOpen(false);
        }} className={`flex items-center justify-between border rounded-md bg-white px-3 py-2 w-44 shadow-sm cursor-pointer transition-all ${dateOpen ? 'border-[#c9a84c] ring-1 ring-[#c9a84c]' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-106">
            <span className="text-gray-700 font-medium truncate" data-id="element-107">
              {dateLabel}
            </span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${dateOpen ? 'rotate-180' : ''}`} data-id="element-108" />
          </button>
          {dateOpen && <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden" data-id="element-109">
              {dateRangeOptions.map(option => <button key={option.key} onClick={() => {
            onDateRangeChange(option.key);
            setDateOpen(false);
          }} className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${dateRangeKey === option.key ? 'bg-[#3d3d3d] text-white' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-110">
                  {option.label}
                </button>)}
            </div>}
        </div>

        {/* Scope Dropdown */}
        <div className="flex flex-col relative" ref={scopeRef} data-id="element-111">
          <span className="text-xs text-gray-500 mb-1 font-medium" data-id="element-112">Scope</span>
          <button onClick={() => {
          setScopeOpen(!scopeOpen);
          setDateOpen(false);
          setSourceOpen(false);
          setStageOpen(false);
        }} className={`flex items-center justify-between border rounded-md bg-white px-3 py-2 w-48 shadow-sm cursor-pointer transition-all ${scopeOpen ? 'border-[#c9a84c] ring-1 ring-[#c9a84c]' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-113">
            <span className="text-gray-700 font-medium truncate" data-id="element-114">
              {selectedScope}
            </span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${scopeOpen ? 'rotate-180' : ''}`} data-id="element-115" />
          </button>
          {scopeOpen && <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden" data-id="element-116">
              {scopeOptions.map(option => <button key={option} onClick={() => {
            onScopeChange(option);
            setScopeOpen(false);
          }} className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${selectedScope === option ? 'bg-[#3d3d3d] text-white' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-117">
                  {option}
                </button>)}
            </div>}
        </div>

        {/* List Source Multi-select */}
        <div className="flex flex-col relative" ref={sourceRef} data-id="element-118">
          <span className="text-xs text-gray-500 mb-1 font-medium" data-id="element-119">
            List Source
          </span>
          <button onClick={() => {
          setSourceOpen(!sourceOpen);
          setDateOpen(false);
          setScopeOpen(false);
          setStageOpen(false);
          if (!sourceOpen) setPendingSources(selectedSources);
        }} className={`flex items-center justify-between border rounded-md bg-white px-3 py-2 w-48 shadow-sm cursor-pointer transition-all ${sourceOpen ? 'border-[#c9a84c] ring-1 ring-[#c9a84c]' : selectedSources.length > 0 ? 'border-[#c9a84c]/60 bg-[#c9a84c]/5' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-120">
            <span className="text-gray-700 font-medium truncate" data-id="element-121">
              {sourceLabel}
            </span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${sourceOpen ? 'rotate-180' : ''}`} data-id="element-122" />
          </button>
          {sourceOpen && <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden" data-id="element-123">
              <div className="py-2 max-h-72 overflow-y-auto" data-id="element-124">
                {leadSourceOptions.map(source => <label key={source} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-125">
                    <input type="checkbox" checked={pendingSources.includes(source)} onChange={() => togglePendingSource(source)} className="rounded border-gray-300 text-[#c9a84c] focus:ring-[#c9a84c] w-4 h-4" data-id="element-126" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-127">
                      {source}
                    </span>
                  </label>)}
              </div>
              <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50" data-id="element-128">
                <button onClick={cancelSourceFilter} className="text-sm font-semibold text-[#5a7fa5] hover:text-[#4a6f95] transition-colors" data-id="element-129">
                  Cancel
                </button>
                <button onClick={applySourceFilter} className="text-sm font-bold text-[#3d3d3d] hover:text-black transition-colors" data-id="element-130">
                  Apply
                </button>
              </div>
            </div>}
        </div>

        {/* Loan Stage Multi-select */}
        <div className="flex flex-col relative" ref={stageRef} data-id="element-131">
          <span className="text-xs text-gray-500 mb-1 flex items-center gap-1 font-medium" data-id="element-132">
            <Layers size={12} data-id="element-133" /> Loan Stage
          </span>
          <button onClick={() => {
          setStageOpen(!stageOpen);
          setDateOpen(false);
          setScopeOpen(false);
          setSourceOpen(false);
          if (!stageOpen) setPendingStages(selectedStages);
        }} className={`flex items-center justify-between border rounded-md bg-white px-3 py-2 w-48 shadow-sm cursor-pointer transition-all ${stageOpen ? 'border-[#c9a84c] ring-1 ring-[#c9a84c]' : selectedStages.length > 0 ? 'border-[#c9a84c]/60 bg-[#c9a84c]/5' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-134">
            <span className="text-gray-700 font-medium truncate" data-id="element-135">
              {stageLabel}
            </span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${stageOpen ? 'rotate-180' : ''}`} data-id="element-136" />
          </button>
          {stageOpen && <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden" data-id="element-137">
              <div className="py-2 max-h-72 overflow-y-auto" data-id="element-138">
                {loanStageOptions.map(option => <label key={option.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-139">
                    <input type="checkbox" checked={pendingStages.includes(option.id)} onChange={() => togglePendingStage(option.id)} className="rounded border-gray-300 text-[#c9a84c] focus:ring-[#c9a84c] w-4 h-4" data-id="element-140" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-141">
                      {option.label}
                    </span>
                  </label>)}
              </div>
              <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50" data-id="element-142">
                <button onClick={cancelStageFilter} className="text-sm font-semibold text-[#5a7fa5] hover:text-[#4a6f95] transition-colors" data-id="element-143">
                  Cancel
                </button>
                <button onClick={applyStageFilter} className="text-sm font-bold text-[#3d3d3d] hover:text-black transition-colors" data-id="element-144">
                  Apply
                </button>
              </div>
            </div>}
        </div>

        {/* View Toggle */}
        <div className="flex flex-col" data-id="element-145">
          <span className="text-xs text-gray-500 mb-1 font-medium" data-id="element-146">View</span>
          <div className="flex items-center border border-gray-300 rounded-md bg-white overflow-hidden shadow-sm" data-id="element-147">
            <button onClick={() => onViewChange('overall')} className={`flex items-center gap-1.5 px-3 py-2 font-medium transition-colors ${activeView === 'overall' ? 'bg-[#3d3d3d] text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-148">
              <List size={14} data-id="element-149" /> Overall
            </button>
            <button onClick={() => onViewChange('bySource')} className={`flex items-center gap-1.5 px-3 py-2 font-medium transition-colors ${activeView === 'bySource' ? 'bg-[#3d3d3d] text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-150">
              <LayoutGrid size={14} data-id="element-151" /> By Source
            </button>
            <button onClick={() => onViewChange('mix')} className={`flex items-center gap-1.5 px-3 py-2 font-medium transition-colors ${activeView === 'mix' ? 'bg-[#3d3d3d] text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-152">
              <PieChart size={14} data-id="element-153" /> Mix
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-5" data-id="element-154">
        <label className="flex items-center gap-2 cursor-pointer group" data-id="element-155">
          <input type="checkbox" checked={includeInactive} onChange={e => onIncludeInactiveChange(e.target.checked)} className="rounded border-gray-300 text-[#c9a84c] focus:ring-[#c9a84c] w-4 h-4 transition-colors" data-id="element-156" />
          <span className="text-gray-600 font-medium group-hover:text-gray-900 transition-colors" data-id="element-157">
            Include Inactive
          </span>
        </label>
        <button onClick={onClear} className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700 font-medium shadow-sm transition-all hover:shadow" data-id="element-158">
          <X size={14} data-id="element-159" /> Clear
        </button>
        <button className="flex items-center gap-1.5 px-4 py-2 bg-[#c9a84c] hover:bg-[#b8973b] text-white rounded-md font-medium shadow-sm transition-all hover:shadow-md active:scale-95" data-id="element-160">
          <RefreshCw size={14} data-id="element-161" /> Refresh
        </button>
      </div>
    </div>;
}